<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="disabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{ props }" name="activator" />
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span>{{ $t('routes.pod.generateLabels') }}</span>
        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="scrollable mt-4 pt-0">
        <div v-if="noThingsSelected" class="bg-error mb-4">
          {{ $t('routes.pod.noSuitableFilesSelected') }}
        </div>

        <v-form ref="form" :disabled="noThingsSelected">
          <div data-cy="podMenu-generatedLabels-selectService">
            <v-radio-group
              v-model="selectedServiceId"
              :label="$t('routes.pod.menuGenerateLabels.selectServiceToUse') + ':'"
              @update:model-value="selectedServiceChanged"
              density="compact"
              class="mt-0 pt-0"
            >
              <v-radio
                v-for="service in metadataServices"
                :key="service.id"
                :label="service.name"
                :value="service.id"
                :disabled="service.id === -1"
                data-cy="dialog-createSubtitle-srtRadio">
              </v-radio>
            </v-radio-group>
          </div>

          <div class="d-flex font-italic text--service-cost">
            <div class="flex-grow-1">{{ $t('routes.pod.menuGenerateLabels.mediaSelected') }}</div>
            <div>{{ videoDuration(summedDuration) }}</div>
          </div>

          <div class="d-flex font-italic text--service-cost">
            <div class="flex-grow-1">{{ $t('routes.pod.yourAvailableCredits') }}</div>
            <div>{{ creditsLeft }}</div>
          </div>

          <div class="d-flex font-italic text--service-cost">
            <div class="flex-grow-1">{{ $t('routes.pod.serviceCost') }}</div>
            <div>{{ summedCost }}</div>
          </div>

          <div class="d-flex font-italic text--service-cost">
            <div class="flex-grow-1">{{ $t('routes.pod.estimatedRemainingCredits') }}</div>
            <div>{{ creditsLeftAfterJob }}</div>
          </div>

          <hr/>

          <div v-if="creditsLeftAfterJob < 0" class="my-2">
            <template v-if="$canPurchaseResources()">
              <div>{{ $t('routes.pod.menuGenerateLabels.wantToBuyBundle') }}</div>
              <div class="d-flex justify-space-around">
                <v-btn v-on:click="dialog = false" class="bg-primary" elevation=0>{{ $t('common.no') }}
                </v-btn>
                <v-btn @click="navigateToUsage" class="bg-primary" elevation=0>
                  {{ $t('common.yes') }}
                </v-btn>
              </div>
            </template>
            <template v-else>
              <div class="bg-warning pa-2">{{ $t('common.contactTenantAdminForCredits') }}</div>
            </template>
          </div>

          <!-- <UtilityPodsErrors></UtilityPodsErrors> -->

          <!-- <div v-for="item in runnablePods" v-bind:key="item.id" class="my-2 d-flex justify-space-between speech-to-text-file">
            <div class="text-truncate" data-cy="podMenu-generatedLabels-runnablePodName">{{item.pod.name}}</div>
            <div v-if="item.metadata && item.metadata.duration">{{ videoDuration(item.metadata.duration) }}</div>
          </div> -->

          <v-list density="compact">
            <v-list-item v-for="item in pods" v-bind:key="item.id">
              <template v-slot:prepend>
                <v-icon v-if="isPodRunnable(item)" color="green">mdi-check-bold</v-icon>
                <v-icon v-else color="red">mdi-close-thick</v-icon>
              </template>

              <v-list-item-title class="d-flex justify-space-between">
                <span>{{ item.name }}</span>
                <span
                  v-if="isPodRunnable(item) && getUtilityPod(item).metadata && typeof getUtilityPod(item).metadata.duration == 'number'"
                  class="mr-4"
                >
                  {{ videoDuration(getUtilityPod(item).metadata.duration) }}
                </span>
              </v-list-item-title>

              <v-list-item-subtitle v-if="getUtilityPod(item) && getUtilityPod(item).error" class="text-error">
                {{ $replaceT(getUtilityPod(item).error.code, getUtilityPod(item).error.params, getUtilityPod(item).error.content) }}
              </v-list-item-subtitle>

              <v-list-item-subtitle v-if="hasNoText(item)" class="text-error">
                {{ $t('backend.ERR42') }}
              </v-list-item-subtitle>

              <template v-slot:append>
                <v-btn
                  v-if="pods.length > 1"
                  icon="mdi-close"
                  variant="text"
                  size="xs-small"
                  @click="toggleSelection(item)"
                />
              </template>

            </v-list-item>
          </v-list>

          <div v-show="successMessage" class="bg-success"
               data-cy="podMenu-generatedLabels-successMessage">{{ successMessage }}
          </div>
          <div v-show="errorMessage" class="bg-error"
               data-cy="podMenu-generatedLabels-errorMessage">{{ errorMessage }}
          </div>

        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <div v-if="creditsLeftAfterJob >= 0" class="mx-2 text-center">
          <v-btn
            v-bind:disabled="isRunJobsDisabled"
            v-on:click.prevent="runJobs()"
            elevation=0
            variant="flat"
            color="primary"
            data-cy="podMenu-generatedLabels-generate-btn"
          >
            {{ $t('routes.pod.menuGenerateLabels.generate') }}
          </v-btn>
        </div>

        <v-btn variant="text" @click="dialog = false">{{ $t('common.close') }}</v-btn>
      </v-card-actions>
    </v-card>

  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useServiceStore } from '@/stores/ServiceStore.js'
import { usePodLabelStore } from '@/stores/PodLabelStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { videoDuration } from '@/components/formatters.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "LabelSelectedDialog",
  props: {
    pods: {
      type: Array,
      required: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      selectedService: null,
      selectedServiceId: '',

      outputLanguage: '',
      startedServices: [],
      preventDoubleClick: false,

      errorMessage: '',
      successMessage: '',
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.updatePodsUtility();
      } else {
        this.resetForm();
      }
    }
  },
  methods: {
    videoDuration,
    resetForm() {
      this.selectedService = null;
      this.selectedServiceId = '';

      this.outputLanguage = '';
      this.startedServices = [];
      this.preventDoubleclick = false;

      this.errorMessage = '';
      this.successMessage = '';

      const serviceStore = useServiceStore();
      serviceStore.resetUtilityPods();
    },
    navigateToUsage() {
      this.dialog = false;
      this.$router.push('/console/usage');
    },
    selectedServiceChanged() {
      this.selectedService = this.metadataServices.find(x => x.id == this.selectedServiceId);
      this.updatePodsUtility();
    },
    toggleSelection(pod) {
      const podStore = usePodStore();
      podStore.toggleSelectedPod(pod);

      this.updatePodsUtility();
    },
    updatePodsUtility() {
      this.errorMessage = '';
      this.successMessage = '';
      this.preventDoubleclick = false;

      if (this.suitablePods.length == 0) {
        const serviceStore = useServiceStore();
        serviceStore.resetUtilityPods();
        return;
      }

      if (!this.selectedService || !this.selectedService.alias) {
        return;
      }

      if (this.startedServices.indexOf(this.selectedService.alias) >= 0) {
        return;
      }

      const payload = {
        serviceAlias: this.selectedService.alias,
        pods: this.suitablePods,
      };

      const serviceStore = useServiceStore();
      serviceStore.updatePodsUtility(payload);
    },
    runJobs: function() {
      this.errorMessage = "";
      this.successMessage = "";

      if (!this.selectedService.alias) {
        return;
      }

      const payload = {
        serviceAlias: this.selectedService.alias,
        utilityPods: this.runnablePods,
      };

      let self = this;
      this.preventDoubleclick = true;

      const serviceStore = useServiceStore();
      serviceStore.runService(payload)
      .then(function() {
        if (self.utilityPodsErrors.length == 0) {
          self.successMessage = self.$t("routes.pod.menuGenerateLabels.operationQueued");
          self.startedServices.push(payload.serviceAlias);
        }
      })
      .catch(function(error) {
        if (error && error.response && error.response.data && error.response.data.error && error.response.data.error.message) {
          self.errorMessage = error.response.data.error.message;
        } else {
          self.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }
      })
      .finally(function() {
        self.preventDoubleClick = false;
      });
    },
  },
  computed: {
    noThingsSelected() {
      return !this.suitablePods || this.suitablePods.length == 0;
    },
    metadataServices: function() {
      let services = [];
      const serviceStore = useServiceStore();

      for(const service of serviceStore.getRekognition) {
        services.push(service);
      }

      return services;
    },
    getMlLabels: function() {
      const podLabelStore = usePodLabelStore();
      return podLabelStore.getMlLabels;
    },
    getAmazonTextDetectedLabels: function() {
      //TODO: filter the ML labels by type 'aws-detecting-text'
      return this.getMlLabels;
    },
    suitablePods: function() {
      let suitablePods = [];

      const podStore = usePodStore();

      for (const pod of podStore.getSelectedVideoPods) {
        suitablePods.push(pod);
      }

      for (const pod of podStore.getSelectedImagePods) {
        suitablePods.push(pod);
      }

      return suitablePods;
    },
    getUtilityPods: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPods;
    },
    utilityPodsErrors: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPodsErrors;
    },
    runnablePods: function() {
      const serviceStore = useServiceStore();
      let runnablePods = serviceStore.getRunnablePods;

      return runnablePods.filter(x => !this.hasNoText(x.pod));
    },
    creditsLeft: function() {
      if (this.getUtilityPods && this.getUtilityPods[0] && this.getUtilityPods[0].metadata) {
        return this.getUtilityPods[0].metadata.available;
      }

      return 0;
    },
    creditsLeftAfterJob: function() {
      return this.creditsLeft - this.summedCost;
    },
    summedDuration: function() {
      let sum = 0;
      for (let i = 0; i < this.getUtilityPods.length; i++) {
        if (this.getUtilityPods[i].metadata &&
            this.getUtilityPods[i].metadata.duration !== null &&
            this.getUtilityPods[i].metadata.duration !== undefined) {
          sum += this.getUtilityPods[i].metadata.duration;
        }
      }
      return sum;
    },
    summedCost: function() {
      let sum = 0;
      for (let i = 0; i < this.getUtilityPods.length; i++) {
        if (this.getUtilityPods[i].metadata &&
            this.getUtilityPods[i].metadata.cost !== null &&
            this.getUtilityPods[i].metadata.cost !== undefined) {
          sum += this.getUtilityPods[i].metadata.cost;
        }
      }
      return sum;
    },
    isPodRunnable: function() {
      return (pod) => {
        const serviceStore = useServiceStore();
        return serviceStore.isPodRunnable(pod) && !this.hasNoText(pod);
      };
    },
    getUtilityPod: function() {
      return (pod) => {
        const serviceStore = useServiceStore();
        return serviceStore.getUtilityPod(pod);
      };
    },
    isRunJobsDisabled: function() {
      if (!this.selectedService ||
          !this.pods || !this.pods.length ||
          this.runnablePods.length == 0 ||
          this.runnablePods.length !== this.pods.length ||
          this.preventDoubleClick == true ||
          this.startedServices.indexOf(this.selectedService.alias) >= 0
      ) {
        return true;
      }

      return false;
    },
    hasNoText: function() {
      const textDetectionServices = ['aws-detecting-text'];

      return (item) => {
        let thePod;
        for (const utilityPod of this.getUtilityPods) {
          if (utilityPod.pod.id === item.id && utilityPod.pod.type === item.type) {
            thePod = utilityPod.pod;
            break;
          }
        }

        if (!this.selectedService || !thePod) {
          return false;
        }

        if (!textDetectionServices.includes(this.selectedService.alias)) {
          return false;
        }

        if (thePod.features && Array.isArray(thePod.features) && thePod.features.length > 0) {
          const hasText = thePod.features.find((o) => o.key === 'hasText');

          if (hasText && hasText.value === 'no') {
            return true;
          }
        }

        return false;
      }
    },
  }
}
</script>

<style scoped>
.text--service-cost {
  font-size: 13px;
  letter-spacing: 1px;
  margin-bottom: 5px;
}

.v-radio-group {
  :deep(& label) {
    margin-inline-start: 10px !important;
  }
}
</style>
